import { AlertTriangle } from "lucide-react";
import { money } from "../lib/finance";
import { accountAllocationSummary } from "../lib/goals";
import { Private, usePrivacy } from "./Privacy";

export interface AccountAllocationBarProps {
  balance: number;
  reserved: number;
  cushion: number;
  currency: string;
  variant?: "card" | "detail";
  availableLabel?: string;
  unfundedLabel?: string;
  unfundedTarget?: number;
}

export default function AccountAllocationBar({
  balance,
  reserved,
  cushion,
  currency,
  variant = "card",
  availableLabel = "Safe to spend",
  unfundedLabel,
  unfundedTarget,
}: AccountAllocationBarProps) {
  const { hidden } = usePrivacy();

  if (reserved <= 0 && cushion <= 0) {
    if (!unfundedLabel || unfundedTarget == null) return null;
    return (
      <div className="account-allocation-wrap">
        <div className="account-allocation-row earmarks">
          <span className="allocation-tag goal">
            <span className="allocation-dot goal" aria-hidden="true" />
            {unfundedLabel}
          </span>
          <span className="allocation-unfunded-target">
            Target <Private>{money(unfundedTarget, currency)}</Private>
          </span>
        </div>
      </div>
    );
  }

  const summary = accountAllocationSummary(balance, reserved, cushion);

  return (
    <div className={`account-allocation-wrap ${variant === "detail" ? "is-detail" : ""}`}>
      {/* Sleek 4px segmented balance bar */}
      <div
        className="account-allocation-track"
        role="img"
        aria-label={`Account allocation bar: goals, cushion, and ${availableLabel.toLowerCase()} balance`}
      >
        {hidden ? (
          <>
            {summary.goalReserved > 0 && (
              <span className="allocation-segment goal" style={{ width: "45%" }} />
            )}
            {summary.cushionAmount > 0 && (
              <span className="allocation-segment cushion" style={{ width: "25%" }} />
            )}
            <span className="allocation-segment safe" style={{ width: "30%" }} />
          </>
        ) : (
          <>
            {summary.goalPercent > 0 && (
              <span
                className="allocation-segment goal"
                style={{ width: `${summary.goalPercent}%` }}
                title={`Goals: ${money(summary.goalReserved, currency)}`}
              />
            )}
            {summary.cushionPercent > 0 && (
              <span
                className="allocation-segment cushion"
                style={{ width: `${summary.cushionPercent}%` }}
                title={`Cushion: ${money(summary.cushionAmount, currency)}`}
              />
            )}
            {summary.safePercent > 0 && (
              <span
                className="allocation-segment safe"
                style={{ width: `${summary.safePercent}%` }}
                title={`${availableLabel}: ${money(summary.safeToSpend, currency)}`}
              />
            )}
          </>
        )}
      </div>

      {/* Compact 2-line legend */}
      <div className="account-allocation-meta">
        {/* Row 1: Earmarks (Goals & Cushion) */}
        <div className="account-allocation-row earmarks">
          <span className="allocation-earmarks-list">
            {summary.goalReserved > 0 && (
              <span className="allocation-tag goal">
                <span className="allocation-dot goal" aria-hidden="true" />
                <Private>{money(summary.goalReserved, currency)}</Private>
                <span className="allocation-tag-name"> goals</span>
              </span>
            )}
            {summary.goalReserved > 0 && summary.cushionAmount > 0 && (
              <span className="allocation-sep" aria-hidden="true">·</span>
            )}
            {summary.cushionAmount > 0 && (
              <span className="allocation-tag cushion">
                <span className="allocation-dot cushion" aria-hidden="true" />
                <Private>{money(summary.cushionAmount, currency)}</Private>
                <span className="allocation-tag-name"> cushion</span>
              </span>
            )}
          </span>

          {summary.shortfall > 0 ? (
            <span className="allocation-shortfall-tag">
              <AlertTriangle size={12} className="allocation-warn-icon" aria-hidden="true" />
              +<Private>{money(summary.shortfall, currency)}</Private> needed
            </span>
          ) : (
            <span className="allocation-locked-total">
              -<Private>{money(summary.totalAllocated, currency)}</Private>
            </span>
          )}
        </div>

        {/* Row 2: The Bottom Line (Safe to spend) */}
        <div className="account-allocation-row safe-to-spend">
          <span className="safe-label">{availableLabel}</span>
          <span className={`safe-value ${summary.safeToSpend <= 0 ? "zero" : ""}`}>
            <Private>{money(summary.safeToSpend, currency)}</Private>
          </span>
        </div>
      </div>
    </div>
  );
}
